import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Tooltip — A high-performance, portal-based cursor follower tooltip.
 */
const Tooltip = ({ children, content }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [coords, setCoords] = useState({ x: 0, y: 0 });
    const tooltipRef = useRef(null);
    const [width, setWidth] = useState(256);

    const handleMouseMove = (e) => {
        setCoords({ x: e.clientX, y: e.clientY });
    };

    useEffect(() => {
        if (isVisible && tooltipRef.current) {
            setWidth(tooltipRef.current.offsetWidth);
        }
    }, [isVisible]);

    const tooltipWidth = width || 256;
    const padding = 16;
    let left = coords.x;

    // Clamp left position so the tooltip doesn't overflow the viewport
    const minLeft = tooltipWidth / 2 + padding;
    const maxLeft = typeof window !== "undefined" ? window.innerWidth - (tooltipWidth / 2 + padding) : 500;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    let arrowLeft = ((coords.x - (left - tooltipWidth / 2)) / tooltipWidth) * 100;
    arrowLeft = Math.max(10, Math.min(arrowLeft, 90));

    return (
        <div 
            className="relative cursor-help"
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
            onMouseMove={handleMouseMove}
        >
            {children}
            {typeof document !== "undefined" && ReactDOM.createPortal(
                <AnimatePresence>
                    {isVisible && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.1, ease: "linear" }}
                            style={{
                                position: "fixed",
                                top: coords.y - 12,
                                left: left,
                                pointerEvents: "none",
                                zIndex: 1000000,
                             }}
                        >
                            <div 
                                ref={tooltipRef}
                                className="bg-slate-900 border border-white/10 shadow-[0_15px_30px_rgba(0,0,0,0.4)] backdrop-blur-xl px-4 py-3 rounded-2xl w-64"
                                style={{
                                    transform: "translate(-50%, -100%)",
                                }}
                            >
                                <p className="text-[11px] font-bold text-white leading-relaxed tracking-tight">
                                    {content}
                                </p>
                                {/* Arrow (Triangle) */}
                                <div 
                                    className="absolute bottom-[-6px] w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-slate-900" 
                                    style={{
                                        left: `${arrowLeft}%`,
                                        transform: "translateX(-50%)"
                                    }}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

export default Tooltip;
